/**
 * testimonialsSlice — Redux Toolkit slice for admin testimonials (CON-CMS-005).
 *
 * Split from cmsSlice to keep each CMS sub-domain's loading/error state
 * independent and to avoid growing cmsSlice's LoadingKey union with
 * testimonial-specific ops.
 *
 * State shape
 * -----------
 * list: TestimonialDTO[]  — ordered by sort_order asc
 * loading: per-operation flags (fetch, create, update, delete, reorder)
 * error:   per-operation nullable strings
 *
 * Thunks
 * ------
 * - fetchTestimonials()          — GET  /api/v1/admin/cms/testimonials
 * - createTestimonial(payload)   — POST /api/v1/admin/cms/testimonials
 * - updateTestimonial({id,...})  — PUT  /api/v1/admin/cms/testimonials/:id
 * - deleteTestimonial(id)        — DELETE /api/v1/admin/cms/testimonials/:id
 * - reorderTestimonials(ids)     — PATCH /api/v1/admin/cms/testimonials/reorder
 *
 * Per `.claude/rules/frontend-data-flow.md` §1, capsules NEVER import
 * this slice; only page-level containers do.
 */
import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { cmsAdminApiClient } from '@dfq/shared/api/admin/cms';
import { isApiError } from '@dfq/shared/api/_envelope';
import type {
  TestimonialDTO,
  CreateTestimonialPayload,
  UpdateTestimonialPayload,
} from '@dfq/shared/types/cms';

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

export type TestimonialsLoadingKey =
  | 'fetchTestimonials'
  | 'createTestimonial'
  | 'updateTestimonial'
  | 'deleteTestimonial'
  | 'reorderTestimonials';

export interface TestimonialsState {
  list: TestimonialDTO[];
  loading: Record<TestimonialsLoadingKey, boolean>;
  error: Record<TestimonialsLoadingKey, string | null>;
}

const initialState: TestimonialsState = {
  list: [],
  loading: {
    fetchTestimonials: false,
    createTestimonial: false,
    updateTestimonial: false,
    deleteTestimonial: false,
    reorderTestimonials: false,
  },
  error: {
    fetchTestimonials: null,
    createTestimonial: null,
    updateTestimonial: null,
    deleteTestimonial: null,
    reorderTestimonials: null,
  },
};

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

export const fetchTestimonials = createAsyncThunk(
  'testimonials/fetch',
  async (_, { rejectWithValue }) => {
    try {
      return await cmsAdminApiClient.fetchTestimonials();
    } catch (err) {
      if (isApiError(err)) return rejectWithValue(err.message);
      return rejectWithValue(err instanceof Error ? err.message : 'Failed to fetch testimonials');
    }
  },
);

export const createTestimonial = createAsyncThunk(
  'testimonials/create',
  async (payload: CreateTestimonialPayload, { rejectWithValue }) => {
    try {
      return await cmsAdminApiClient.createTestimonial(payload);
    } catch (err) {
      if (isApiError(err)) return rejectWithValue({ message: err.message, errors: err.errors });
      return rejectWithValue({ message: err instanceof Error ? err.message : 'Failed to create' });
    }
  },
);

export const updateTestimonial = createAsyncThunk(
  'testimonials/update',
  async (
    { id, ...payload }: { id: number } & UpdateTestimonialPayload,
    { rejectWithValue },
  ) => {
    try {
      return await cmsAdminApiClient.updateTestimonial(id, payload);
    } catch (err) {
      if (isApiError(err)) return rejectWithValue({ message: err.message, errors: err.errors });
      return rejectWithValue({ message: err instanceof Error ? err.message : 'Failed to update' });
    }
  },
);

export const deleteTestimonial = createAsyncThunk(
  'testimonials/delete',
  async (id: number, { rejectWithValue }) => {
    try {
      await cmsAdminApiClient.deleteTestimonial(id);
      return id;
    } catch (err) {
      if (isApiError(err)) return rejectWithValue(err.message);
      return rejectWithValue(err instanceof Error ? err.message : 'Failed to delete');
    }
  },
);

export const reorderTestimonials = createAsyncThunk(
  'testimonials/reorder',
  async (orderedIds: number[], { rejectWithValue }) => {
    try {
      await cmsAdminApiClient.reorderTestimonials({ ordered_ids: orderedIds });
      return orderedIds;
    } catch (err) {
      if (isApiError(err)) return rejectWithValue(err.message);
      return rejectWithValue(err instanceof Error ? err.message : 'Failed to reorder');
    }
  },
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

export const testimonialsSlice = createSlice({
  name: 'testimonials',
  initialState,
  reducers: {
    /** Optimistically patch a single testimonial in the list. */
    patchTestimonial(state, action: PayloadAction<Partial<TestimonialDTO> & { id: number }>) {
      const idx = state.list.findIndex((t) => t.id === action.payload.id);
      if (idx !== -1) {
        state.list[idx] = { ...state.list[idx], ...action.payload };
      }
    },
    /** Optimistically reorder the list by a new ordered ids array. */
    reorderOptimistic(state, action: PayloadAction<number[]>) {
      const byId = new Map(state.list.map((t) => [t.id, t]));
      state.list = action.payload.flatMap((id) => {
        const t = byId.get(id);
        return t ? [t] : [];
      });
    },
    /** Restore the list to a prior snapshot (revert after failed reorder). */
    restoreList(state, action: PayloadAction<TestimonialDTO[]>) {
      state.list = action.payload;
    },
  },
  extraReducers: (builder) => {
    // ── fetchTestimonials ──────────────────────────────────────────────────
    builder
      .addCase(fetchTestimonials.pending, (state) => {
        state.loading.fetchTestimonials = true;
        state.error.fetchTestimonials = null;
      })
      .addCase(fetchTestimonials.fulfilled, (state, action) => {
        state.loading.fetchTestimonials = false;
        state.list = [...action.payload].sort((a, b) => a.sort_order - b.sort_order);
      })
      .addCase(fetchTestimonials.rejected, (state, action) => {
        state.loading.fetchTestimonials = false;
        state.error.fetchTestimonials =
          (action.payload as string | undefined) ??
          action.error.message ??
          'Failed to fetch testimonials';
      });

    // ── createTestimonial ──────────────────────────────────────────────────
    builder
      .addCase(createTestimonial.pending, (state) => {
        state.loading.createTestimonial = true;
        state.error.createTestimonial = null;
      })
      .addCase(createTestimonial.fulfilled, (state, action) => {
        state.loading.createTestimonial = false;
        state.list = [...state.list, action.payload].sort(
          (a, b) => a.sort_order - b.sort_order,
        );
      })
      .addCase(createTestimonial.rejected, (state, action) => {
        state.loading.createTestimonial = false;
        const payload = action.payload as { message: string } | undefined;
        state.error.createTestimonial =
          payload?.message ?? action.error.message ?? 'Failed to create testimonial';
      });

    // ── updateTestimonial ──────────────────────────────────────────────────
    builder
      .addCase(updateTestimonial.pending, (state) => {
        state.loading.updateTestimonial = true;
        state.error.updateTestimonial = null;
      })
      .addCase(updateTestimonial.fulfilled, (state, action) => {
        state.loading.updateTestimonial = false;
        const idx = state.list.findIndex((t) => t.id === action.payload.id);
        if (idx !== -1) {
          state.list[idx] = action.payload;
        }
      })
      .addCase(updateTestimonial.rejected, (state, action) => {
        state.loading.updateTestimonial = false;
        const payload = action.payload as { message: string } | undefined;
        state.error.updateTestimonial =
          payload?.message ?? action.error.message ?? 'Failed to update testimonial';
      });

    // ── deleteTestimonial ──────────────────────────────────────────────────
    builder
      .addCase(deleteTestimonial.pending, (state) => {
        state.loading.deleteTestimonial = true;
        state.error.deleteTestimonial = null;
      })
      .addCase(deleteTestimonial.fulfilled, (state, action) => {
        state.loading.deleteTestimonial = false;
        state.list = state.list.filter((t) => t.id !== action.payload);
      })
      .addCase(deleteTestimonial.rejected, (state, action) => {
        state.loading.deleteTestimonial = false;
        state.error.deleteTestimonial =
          (action.payload as string | undefined) ??
          action.error.message ??
          'Failed to delete testimonial';
      });

    // ── reorderTestimonials ────────────────────────────────────────────────
    builder
      .addCase(reorderTestimonials.pending, (state) => {
        state.loading.reorderTestimonials = true;
        state.error.reorderTestimonials = null;
      })
      .addCase(reorderTestimonials.fulfilled, (state) => {
        state.loading.reorderTestimonials = false;
      })
      .addCase(reorderTestimonials.rejected, (state, action) => {
        state.loading.reorderTestimonials = false;
        state.error.reorderTestimonials =
          (action.payload as string | undefined) ??
          action.error.message ??
          'Failed to reorder testimonials';
      });
  },
});

export const { patchTestimonial, reorderOptimistic, restoreList } = testimonialsSlice.actions;
export const testimonialsReducer = testimonialsSlice.reducer;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export interface TestimonialsRootState {
  testimonials: TestimonialsState;
}

export const selectTestimonials = (state: TestimonialsRootState): TestimonialDTO[] =>
  state.testimonials.list;

export const selectTestimonialsLoading = (
  state: TestimonialsRootState,
): Record<TestimonialsLoadingKey, boolean> => state.testimonials.loading;

export const selectTestimonialsError = (
  state: TestimonialsRootState,
): Record<TestimonialsLoadingKey, string | null> => state.testimonials.error;
